import type { RideDetail, RidePatch } from './ridesApi'

export type PhotoDraft = { id: number; kind: 'finished' | 'build' }
export type Draft = {
  title: string; story: string; class_slug: string; started_from_text: string; home_track: string
  parts: { type: string; text: string }[]; photos: PhotoDraft[]; cover: number | null
}
export type DraftState = { draft: Draft | null; base: Draft | null }

export const toDraft = (r: RideDetail): Draft => ({
  title: r.title, story: r.story, class_slug: r.class_slug, started_from_text: r.started_from_text, home_track: r.home_track ?? '',
  parts: r.parts.map((p) => ({ type: p.type, text: p.text })),
  photos: r.photos.map((p) => ({ id: p.id, kind: p.kind })),
  cover: r.photos.find((p) => p.is_cover)?.id ?? null,
})

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
export const isDirty = (s: DraftState): boolean => s.draft !== null && !same(s.draft, s.base)

/** A fresh load replaces the draft only when the editor has nothing unsaved. */
export function reconcile(s: DraftState, incoming: Draft): DraftState {
  return isDirty(s) ? s : { draft: incoming, base: incoming }
}

/** Only the keys that differ from the loaded ride. */
export function diffPatch(d: Draft, base: Draft): RidePatch {
  const p: RidePatch = {}
  if (d.title !== base.title) p.title = d.title
  if (d.story !== base.story) p.story = d.story
  if (d.class_slug !== base.class_slug) p.class_slug = d.class_slug
  if (d.started_from_text !== base.started_from_text) p.started_from_text = d.started_from_text
  if (d.home_track !== base.home_track) p.home_track = d.home_track.trim() || null
  if (!same(d.parts, base.parts)) p.parts = d.parts.filter((x) => x.text.trim())
  if (!same(d.photos, base.photos)) p.photos = d.photos
  if (d.cover !== base.cover && d.cover != null) p.cover_photo_id = d.cover
  return p
}

/** After a save: the server's copy becomes the base; any field edited since the save was sent keeps the editor's value. */
export function afterSave(current: Draft, sent: Draft, saved: Draft): DraftState {
  const out = { ...saved } as Record<keyof Draft, unknown>
  for (const k of Object.keys(saved) as (keyof Draft)[]) {
    if (!same(current[k], sent[k])) out[k] = current[k]
  }
  return { draft: out as Draft, base: saved }
}
